import axios from 'axios'

import { CartOperationInProgressReason } from '@/types/CartOperationInProgressReason'

import { feedbackContent } from '@/content/feedback'

// Sunny16 returns 400 with a bare-string body when a call targets a cart
// bundle the cart no longer has. A DELETE that raced the first response
// (duplicate tap) already matches intent, so that caller treats it as a
// no-op; an upsert carrying a stale id recovers by resending as an insert.
export function isCartBundleNotFoundError(error: unknown): boolean {
  if (!axios.isAxiosError<string>(error)) return false
  if (error.response?.status !== 400) return false
  const body = error.response.data
  return typeof body === 'string' && body.includes('Cart Bundle not found')
}

// Sunny16 surfaces EF Core's optimistic-concurrency exception verbatim as a
// 400 when two cart-mutating writes race on the same cart row (e.g. duplicate
// taps on an Add-to-Cart CTA before the first response lands). The losing
// call's intended state matches what the winning call already produced for
// same-button rapid taps, so callers treat it as a no-op. The distinctive
// `expected to affect 1 row(s)` phrase is the most stable marker in the
// EF Core message.
export function isCartUpsertConcurrencyError(error: unknown): boolean {
  if (!axios.isAxiosError<string>(error)) return false
  if (error.response?.status !== 400) return false
  const body = error.response.data
  return typeof body === 'string' && body.includes('expected to affect 1 row(s)')
}

// Sunny16 returns 400 with { type: "CartDependencyError", message: "..." } when a bundle
// requires another item to be present in the cart first. Expected and handled by callers
// so New Relic doesn't treat it as an unhandled error.
export function isCartDependencyError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false
  if (error.response?.status !== 400) return false
  return error.response.data?.type === 'CartDependencyException'
}

export function cartDependencyErrorMessage(error: unknown): string | null {
  if (!isCartDependencyError(error)) return null
  return (axios.isAxiosError(error) && error.response?.data?.message) || null
}

// Sunny16 returns 409 with { type: "CartOperationInProgressException", reason, message } when
// another request (usually another tab) holds the cart. A 409, so it never matches the 400
// concurrency no-op above: the customer's change did not happen and they need to be told.
export function isCartOperationInProgressError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false
  if (error.response?.status !== 409) return false
  return error.response.data?.type === 'CartOperationInProgressException'
}

/** A busy-cart 409 because another request is changing the cart (`reason: "Mutation"`). */
export function isCartMutationInProgressError(error: unknown): boolean {
  return (
    isCartOperationInProgressError(error) &&
    axios.isAxiosError(error) &&
    error.response?.data?.reason === CartOperationInProgressReason.Mutation
  )
}

const CART_OPERATION_IN_PROGRESS_COPY: Record<CartOperationInProgressReason, string> = {
  [CartOperationInProgressReason.Mutation]: feedbackContent.cartBeingUpdated,
  [CartOperationInProgressReason.Payment]: feedbackContent.cartPaymentInProgress
}

/**
 * Customer-facing copy for a busy-cart 409, or null when `error` is something else. The copy is
 * ours, keyed on `reason`; the server's `message` covers a reason this storefront does not know.
 */
export function cartOperationInProgressMessage(error: unknown): string | null {
  if (!isCartOperationInProgressError(error) || !axios.isAxiosError(error)) return null
  const { reason, message } = error.response!.data as { reason?: string; message?: string }

  return (
    CART_OPERATION_IN_PROGRESS_COPY[reason as CartOperationInProgressReason] ??
    message ??
    CART_OPERATION_IN_PROGRESS_COPY[CartOperationInProgressReason.Mutation]
  )
}

// Sunny16 throws `Exception("Cart not found")` when the cart id and security key do not
// resolve, and `HandleException` maps every exception to a 400 with the message as the body.
// Distinct from `Cart Bundle not found`, which is about a bundle inside a cart that does exist.
export function isCartGoneError(error: unknown): boolean {
  if (!axios.isAxiosError<string>(error)) return false
  const status = error.response?.status
  if (status !== 400 && status !== 404) return false
  const body = error.response?.data
  return typeof body === 'string' && body.includes('Cart not found')
}

/**
 * A cart write the client abandoned before the server answered. Axios reports its own
 * timeout as `ECONNABORTED` (`ETIMEDOUT` on some builds), with no response attached.
 */
export function isRequestTimeoutError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false
  if (error.response) return false
  return error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT'
}

/**
 * A cart write timed out, so whether the server applied it is **unknown** — not failed. The
 * server may well have committed it, which is how a cart ends up permanently out of sync with
 * the storefront's persisted copy (2026-09-16 incident). Thrown after the cart has been
 * refetched, so callers can say "we couldn't confirm" rather than "it failed".
 */
export class CartWriteUnconfirmedError extends Error {
  readonly cause?: unknown

  constructor(options?: { cause?: unknown }) {
    super('Cart write timed out; the cart has been refetched from the server')
    this.name = 'CartWriteUnconfirmedError'
    this.cause = options?.cause
  }
}

/** Customer-facing copy for an unconfirmed cart write, or null when `error` is something else. */
export function cartWriteUnconfirmedMessage(error: unknown): string | null {
  if (!(error instanceof CartWriteUnconfirmedError)) return null
  return feedbackContent.cartChangeUnconfirmed
}

/**
 * Customer-facing copy for a failed cart change: the busy-cart or unconfirmed-write message when
 * `error` is one of those, and `fallback` otherwise.
 */
export function cartChangeErrorMessage(error: unknown, fallback: string): string {
  return cartOperationInProgressMessage(error) ?? cartWriteUnconfirmedMessage(error) ?? fallback
}

/**
 * A mutually-exclusive selection swap (`useBundleListItem`'s `selectionGroup`) failed partway: the
 * chosen bundle was added but clearing the previously-selected sibling failed. The caller undoes its
 * own add before throwing this, so the cart is back to the prior selection — the customer's action
 * simply didn't take effect, which is NOT "failed to add" (the add worked). Carries the underlying
 * failure as `cause`.
 */
export class SelectionSwapError extends Error {
  // Set explicitly rather than via `super(message, { cause })` — the ES2022
  // Error-options constructor isn't in this project's TS lib target.
  readonly cause?: unknown

  constructor(message: string, options?: { cause?: unknown }) {
    super(message)
    this.name = 'SelectionSwapError'
    this.cause = options?.cause
  }
}

/**
 * Customer-facing copy for a failed selection swap, or null when `error` is something else.
 * Consumed by `useCartMutationFeedback` so the toast describes what actually happened.
 */
export function selectionSwapErrorMessage(error: unknown): string | null {
  if (!(error instanceof SelectionSwapError)) return null
  return feedbackContent.selectionSwapFailed
}
